import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, UtensilsCrossed, User } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toast } from '../store/toastStore';
import { config } from '../config/env';
import { RequestError } from '../types/api';
import { Button, Input } from '../components/ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const schema = z.object({
  identifier: z.string().trim().min(3, 'Enter your email or mobile number'),
  password: z.string().min(1, 'Enter your password'),
});

type FormValues = z.infer<typeof schema>;

const DEMO_LOGINS = [
  { role: 'Owner', id: 'admin@spicehub.test', pw: 'Admin@123' },
  { role: 'Cashier', id: 'cashier@spicehub.test', pw: 'Cashier@123' },
  { role: 'Kitchen', id: 'kitchen@spicehub.test', pw: 'Kitchen@123' },
];

export function LoginPage() {
  useDocumentTitle('Sign in');
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const signIn = useAuthStore((s) => s.signIn);
  const signingIn = useAuthStore((s) => s.signingIn);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    try {
      await signIn(values.identifier, values.password);
      toast.success('Welcome back!');
      const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      const message =
        error instanceof RequestError
          ? error.message
          : 'Unable to sign in right now. Please try again.';
      toast.error(message);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-ink-900 via-ink-900 to-brand-900 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-pop">
            <UtensilsCrossed className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-bold text-white">Fast Food POS</h1>
          <p className="text-sm text-ink-300">Sign in to start taking orders</p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-pop sm:p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Input
              label="Email or mobile"
              autoComplete="username"
              autoFocus
              leftIcon={<User className="h-5 w-5" />}
              error={errors.identifier?.message}
              {...register('identifier')}
            />
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              leftIcon={<Lock className="h-5 w-5" />}
              error={errors.password?.message}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-lg p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              }
              {...register('password')}
            />
            <Button type="submit" size="lg" fullWidth loading={signingIn}>
              Sign in
            </Button>
          </form>

          {config.showDemoLogins && (
            <div className="mt-6 rounded-xl border border-dashed border-ink-200 bg-ink-50 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                Demo logins (dev)
              </p>
              <div className="grid gap-1.5">
                {DEMO_LOGINS.map((d) => (
                  <button
                    key={d.role}
                    type="button"
                    onClick={() => {
                      setValue('identifier', d.id, { shouldValidate: true });
                      setValue('password', d.pw, { shouldValidate: true });
                    }}
                    className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-left text-xs hover:bg-brand-50"
                  >
                    <span className="font-semibold text-ink-800">{d.role}</span>
                    <span className="text-ink-500">{d.id}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
